import {
  CardNote,
  SectionLabel,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useTournamentLeaderboard } from "@/hooks/tournaments/use-tournament-leaderboard";
import { useTranslation } from "react-i18next";
import { PrizeText } from "@/components/prize-text";
import { useId } from "react";

/**
 * Sidebar standing card — the player's position/score, current prize, and how far
 * they are from the prize zone. Figma 5129:6837.
 */
export const YourStandingCard = ({
  campaignId,
  density = "default",
}: {
  campaignId?: string | number;
  /** `dock`: the overlay's narrow column — adds a bar towards the prize zone,
   *  labelled by how far there is to go, or by the zone itself once inside. */
  density?: "default" | "dock";
}) => {
  const { t } = useTranslation();
  const statusId = useId();
  const id = campaignId != null ? String(campaignId) : undefined;
  const {
    playerData,
    fullLeaderboardData,
    scoreLabel,
    showScoreMultiplierSuffix,
    raceSpins,
    finished,
  } = useTournamentLeaderboard(id);

  const score = playerData?.score ?? 0;
  const currentPrize = playerData?.prize;

  // The lowest prize-winning rank's score is the threshold to reach the prize zone.
  const prizeEntries = fullLeaderboardData.filter(
    (e) => e.prize && !e.isEmptySlot
  );
  const lastPrizeEntry = prizeEntries.length
    ? prizeEntries.reduce((a, b) => (a.position > b.position ? a : b))
    : undefined;
  const pointsToZone =
    lastPrizeEntry && !currentPrize
      ? Math.max(0, lastPrizeEntry.score - score)
      : 0;

  // Over: the standing is final. A winner is told what they won (not a "current" prize), a
  // non-winner just keeps their final position and score — no prize row, no "how far to go".
  const won = finished && Boolean(currentPrize);

  // In the prizes: no line — the current prize above already says it. Outside: how far to go —
  // unless a race has used every spin, when there is nothing left to earn with.
  const hint = finished
    ? null
    : currentPrize
      ? null
      : raceSpins?.left === 0
        ? t("tournament.noSpinsLeft", "No spins left — your score is final")
        : lastPrizeEntry
          ? // Highest-win tournaments score by a single multiplier, so "earn N pts"
            // doesn't apply — point at the threshold multiplier to reach instead.
            showScoreMultiplierSuffix
            ? t(
                "tournament.reachToZone",
                "Reach {{score}} to enter the prize zone",
                {
                  score: `${lastPrizeEntry.score.toLocaleString()}x`,
                }
              )
            : t(
                "tournament.earnToZone",
                "Earn {{points}} pts to enter the prize zone",
                {
                  points: pointsToZone.toLocaleString(),
                }
              )
          : t(
              "tournament.placeQualifyingBet",
              "Place a qualifying bet for a chance to win"
            );

  const dock = density === "dock";
  // The dock keeps a line even inside the prizes: it labels the bar, in the
  // progress colour.
  const status =
    dock && !finished && currentPrize
      ? t("tournament.inPrizeZone", "You're in the prize zone")
      : hint;
  // How far the score has come towards the last prize-winning rank's score;
  // full once inside the zone.
  const progress = currentPrize
    ? 100
    : lastPrizeEntry && lastPrizeEntry.score > 0
      ? Math.min(100, Math.round((score / lastPrizeEntry.score) * 100))
      : 0;

  return (
    <Card className="flex flex-col items-start gap-3">
      {/* Bottom-aligned: a label that wraps (a race's "Spins left" beside two others) grows
          upwards, so the figures stay on one line. */}
      <div
        className={cn(
          "flex w-full items-end justify-center",
          dock ? "gap-2" : "gap-4"
        )}
      >
        <Stat
          label={t("tournament.position", "Position")}
          value={playerData ? String(playerData.position) : "—"}
          density={density}
        />
        <Stat
          label={scoreLabel}
          value={`${score.toLocaleString()}${showScoreMultiplierSuffix ? "x" : ""}`}
          // Only highlight a real score in gold; a 0 shouldn't read as an
          // achievement (gold is the prize/achievement colour).
          valueClassName={score > 0 ? "text-prizes" : undefined}
          density={density}
        />
        {raceSpins && (
          <Stat
            label={t("tournament.spinsLeft", "Spins left")}
            value={`${raceSpins.left.toLocaleString()} / ${raceSpins.total.toLocaleString()}`}
            density={density}
          />
        )}
      </div>
      {dock && !finished && lastPrizeEntry && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          aria-labelledby={status ? statusId : undefined}
          className="h-1.5 w-full overflow-hidden rounded-full bg-background-card-surface"
        >
          <div
            className="h-full rounded-full bg-active transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {dock && status && (
        <p
          id={statusId}
          className={cn(
            "w-full text-left font-body text-xs",
            currentPrize ? "font-semibold text-active" : "text-muted-foreground"
          )}
        >
          {status}
        </p>
      )}
      {won ? (
        // The win, unmistakable: its own tinted strip in the progress colour, "You won" + the prize.
        <div className="flex w-full items-center justify-between gap-2 rounded-(--border-radius-inner) border border-active/40 bg-active/10 px-3 py-2.5">
          <p className="font-heading text-[12px] leading-4 font-bold text-active lg:text-[13px] lg:leading-[18px]">
            {t("prizes.youWon", "You won")}
          </p>
          <PrizeText
            size="inherit"
            weight="bold"
            icon
            fit="marquee"
            className="min-w-0 text-[15px] leading-5 lg:text-[17px]"
          >
            {currentPrize}
          </PrizeText>
        </div>
      ) : (
        !finished && (
          <>
            <div className="h-px w-full bg-foreground/[0.06]" />
            <div className="flex w-full items-center justify-between gap-2">
              <p className="font-heading text-[12px] leading-4 font-semibold text-foreground-secondary lg:text-[13px] lg:leading-[18px]">
                {t("prizes.currentPrize", "Current prize")}
              </p>
              <PrizeText
                size="inherit"
                weight="semibold"
                // The prize mark only on a prize: no prize yet is a plain dash, not a starred one.
                icon={Boolean(currentPrize)}
                tone={currentPrize ? undefined : "muted"}
                fit="marquee"
                className="min-w-0 text-[14px] leading-[18px] lg:text-[16px] lg:leading-5"
              >
                {currentPrize ?? "—"}
              </PrizeText>
            </div>
          </>
        )
      )}
      {!dock && hint && <CardNote>{hint}</CardNote>}
    </Card>
  );
};

const Stat = ({
  label,
  value,
  valueClassName,
  density,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  /** `dock`: the overlay's narrow column — the figure keeps the phone size at every width, and
   *  each column sizes to its content, so the widest figure ("498 / 500") gets the room and only
   *  the labels wrap. */
  density: "default" | "dock";
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-0.5",
      density === "dock" ? "flex-auto" : "min-w-0 flex-1"
    )}
  >
    <SectionLabel>{label}</SectionLabel>
    <p
      className={cn(
        "font-heading text-[18px] leading-7 font-semibold tracking-[-0.44px] text-foreground",
        density === "dock"
          ? "whitespace-nowrap"
          : "lg:text-[22px] lg:leading-[38px]",
        valueClassName
      )}
    >
      {value}
    </p>
  </div>
);
